import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2 } from "lucide-react";

const TOPIC_VALUES = ["ai-strategy", "ai-literacy", "governance", "other"] as const;
type TopicValue = (typeof TOPIC_VALUES)[number];

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.nameRequired") })
          .max(100, { message: t("contactForm.errors.nameMax") }),
        email: z
          .string()
          .trim()
          .email({ message: t("contactForm.errors.emailInvalid") })
          .max(255, { message: t("contactForm.errors.emailMax") }),
        company: z
          .string()
          .trim()
          .max(100, { message: t("contactForm.errors.companyMax") })
          .optional(),
        phone: z
          .string()
          .trim()
          .max(20, { message: t("contactForm.errors.phoneMax") })
          .optional(),
        topic: z.enum(TOPIC_VALUES, {
          errorMap: () => ({ message: t("contactForm.errors.topicRequired") }),
        }),
        message: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.messageRequired") })
          .max(1000, { message: t("contactForm.errors.messageMax") }),
      }),
    [t]
  );

  type ContactFormValues = z.infer<typeof contactSchema>;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      topic: undefined as unknown as TopicValue,
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setSubmitting(true);
    const language = i18n.language?.startsWith("sv") ? "sv" : "en";
    const id = crypto.randomUUID();
    const topicLabel = t(`contactForm.fields.topic.options.${data.topic}`);

    try {
      const { error: insertError } = await supabase
        .from("contact_submissions")
        .insert({
          id,
          name: data.name,
          email: data.email,
          company: data.company || null,
          phone: data.phone || null,
          topic: data.topic,
          message: data.message,
          language,
          user_agent: navigator.userAgent.slice(0, 512),
          page_url: window.location.href.slice(0, 2048),
        });

      if (insertError) throw insertError;

      trackEvent("generate_lead", {
        form_location: "contact_page",
        topic: data.topic,
        has_company: Boolean(data.company),
        has_phone: Boolean(data.phone),
      });

      // Fire-and-track both emails; failures don't break the submission since data is saved.
      const submittedAt = new Date().toLocaleString(language === "sv" ? "sv-SE" : "en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      });

      const sendConfirmation = supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: data.email,
          submissionId: id,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: data.name, language, topicLabel },
        },
      });

      const sendNotification = supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          submissionId: id,
          idempotencyKey: `contact-notify-${id}`,
          templateData: {
            name: data.name,
            email: data.email,
            company: data.company || "",
            phone: data.phone || "",
            topicLabel,
            message: data.message,
            language,
            submittedAt,
            pageUrl: window.location.href,
          },
        },
      });

      const [confirmRes, notifyRes] = await Promise.allSettled([sendConfirmation, sendNotification]);
      if (confirmRes.status === "rejected") console.error("Confirmation email failed", confirmRes.reason);
      if (notifyRes.status === "rejected") console.error("Notification email failed", notifyRes.reason);

      toast.success(t("contactForm.toastSuccess", { email: data.email }));
      setSubmittedEmail(data.email);
      form.reset();
    } catch (err) {
      console.error("Contact form submission failed", err);
      toast.error(t("contactForm.toastError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="mx-auto mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          {t("contactForm.success.title")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("contactForm.success.body", { email: submittedEmail })}
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setSubmittedEmail(null)}
        >
          {t("contactForm.success.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      <h2 className="text-2xl md:text-3xl font-bold mb-6">{t("contactForm.heading")}</h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.name.label")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.name.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.email.label")} *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("contactForm.fields.email.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("contactForm.fields.company.label")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("contactForm.fields.company.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("contactForm.fields.phone.label")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("contactForm.fields.phone.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="topic"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.topic.label")} *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("contactForm.fields.topic.placeholder")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TOPIC_VALUES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`contactForm.fields.topic.options.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.message.label")} *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t("contactForm.fields.message.placeholder")}
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={submitting}>
            {submitting ? t("contactForm.submitting") : t("contactForm.submit")}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
